import { getCurrencySymbol, getDurationType, PAYMENT_TYPE } from '@/constants/market'
import usePremiumPlansData from '@/hooks/usePremiumPlansData'
import { getPremiumPlanDiscount } from '@/lib/premium-plan-discount'
import type {
  PlanDetail,
  PlanDetailProduct,
  PlanDetails,
  PlanList,
  PlanListItem,
} from '@/types/market'
import { useMemo } from 'react'

export type PlanSummary = {
  id: string
  name: string
  price: string
  currency: string
  currencySymbol: string
  description: string
  voucherId: string
  paymentType: string
  durationUnit?: string
  standardId: string
  /** Set only while a configured discount applies; `price` is then the discounted price. */
  originalPrice?: string
  discountPercent?: number
}

const DEFAULT_SUMMARY = {
  id: '',
  name: '',
  price: '—',
  currency: '',
  currencySymbol: '',
  description: '',
  voucherId: '',
  paymentType: '',
  standardId: '',
} as const

const getFirst = <T>(xs?: T[] | null): T | undefined =>
  Array.isArray(xs) && xs.length > 0 ? xs[0] : undefined
const getItems = (list: PlanList | null): PlanListItem[] => list?.data?.products?.items ?? []
const getProductFromDetail = (detail: PlanDetail | null) => detail?.product
const getLayoutFromDetail = (detail: PlanDetail | null) => detail?.layout

const pickPrice = (product: PlanDetailProduct | undefined): { currency: string; price: string } => {
  const currency = product?.currency?.kind ?? DEFAULT_SUMMARY.currency
  const rawPrice = product?.payment?.purchase?.price
  const price = rawPrice != null ? String(rawPrice) : DEFAULT_SUMMARY.price
  return { currency, price }
}

const pickStandardId = (product: PlanDetailProduct | undefined): string => {
  const standardId = product?.content?.standardId
  return standardId != null ? String(standardId) : DEFAULT_SUMMARY.standardId
}

const pickDurationUnit = (product: PlanDetailProduct | undefined): string | undefined => {
  const durationUnit = product?.payment?.subscriptionInfo?.plan?.DurationUnit
  return durationUnit != null ? getDurationType(durationUnit) : undefined
}

const pickVoucherId = (product: PlanDetailProduct | undefined): string => {
  const v = getFirst(product?.content?.attributes)?.value
  return v != null ? String(v) : DEFAULT_SUMMARY.voucherId
}

function findDetailByPlanId(
  planId: string | number,
  detailRaw: PlanDetails | null,
): PlanDetail | undefined {
  return detailRaw?.datas?.find((d) => String(d?.product?.content?.id ?? '') === String(planId))
}

const applyPremiumPlanDiscount = (plan: PlanSummary): PlanSummary => {
  const discount = getPremiumPlanDiscount(plan.id, plan.currency)
  if (!discount) return plan
  return {
    ...plan,
    price: discount.finalPrice,
    originalPrice: discount.originalPrice,
    discountPercent: discount.discountPercent,
  }
}

const buildPlan = (listItem: PlanListItem | null, planDetail: PlanDetail | null): PlanSummary => {
  if (!listItem || !planDetail) return DEFAULT_SUMMARY
  const id = String(listItem?.id ?? DEFAULT_SUMMARY.id)
  const paymentType = listItem?.payment_type ?? DEFAULT_SUMMARY.paymentType
  const layout = getLayoutFromDetail(planDetail)
  const name = layout?.name ?? DEFAULT_SUMMARY.name
  const description = layout?.desc ?? DEFAULT_SUMMARY.description
  const product = getProductFromDetail(planDetail)
  const { currency, price } = pickPrice(product)
  const voucherId = pickVoucherId(product)
  const currencySymbol = getCurrencySymbol(currency)
  const durationUnit =
    paymentType === PAYMENT_TYPE.subscription ? pickDurationUnit(product) : undefined
  const standardId = pickStandardId(product)

  return applyPremiumPlanDiscount({
    id,
    name,
    paymentType,
    description,
    currency,
    currencySymbol,
    price,
    durationUnit,
    voucherId,
    standardId,
  })
}

const usePremiumPlansView = (creatorId: string, enabled: boolean = false) => {
  const {
    planList = null,
    planDetails = null,
    isLoading,
    isError,
    isEmpty: isDataEmpty,
  } = usePremiumPlansData(creatorId, enabled)

  const premiumPlans = useMemo(() => {
    if (isDataEmpty || !planList || !planDetails) return [DEFAULT_SUMMARY] as PlanSummary[]
    const items = getItems(planList)
    return items
      .map((listItem) => {
        if (!listItem.id) return []
        const detailByPlanId = findDetailByPlanId(listItem.id, planDetails)
        if (!detailByPlanId) return []
        return buildPlan(listItem, detailByPlanId)
      })
      .filter(Boolean) as PlanSummary[]
  }, [isDataEmpty, planList, planDetails])
  const isEmpty = isDataEmpty

  return { premiumPlans, isLoading, isError, isEmpty }
}

export default usePremiumPlansView
